import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  LinearProgress,
  Modal,
  TextField,
  InputLabel,
  IconButton,
  CircularProgress,
  Stack,
  Button,
  Tooltip,
  Skeleton,
} from "@mui/material";
import {
  // CheckCircleOutline,
  Circle,
  CloseOutlined,
  // HighlightOff,
} from "@mui/icons-material";
import ButtonCommon from "../../CommonComponent/ButtonCommon";
import { fetchUserAttributes } from "aws-amplify/auth";
import { contactSales, getLicensingUsageDetails } from "../../api/licensing";
import { showErrorToast, showSuccessToast } from "../../toastUtils";
import { useSelector } from "react-redux";
import { Upgrade } from "../../assets/svg";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { ReactComponent as Scroll } from "../../assets/svg/scroll.svg";
import { ReactComponent as Profile } from "../../assets/svg/profile.svg";
import { ReactComponent as Hierarchy } from "../../assets/svg/hierarchy.svg";
import { ReactComponent as Frame } from "../../assets/svg/frame.svg";
import { ReactComponent as PageView } from "../../assets/svg/pageView.svg";
import {
  CheckCircleOutline,
  HighlightOff,
  // ... your other imports
} from "@mui/icons-material";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import CommonHeader from "../../CommonComponent/CommonHeader";
import { getWebsiteById } from "../../api/websiteService";
import formatNumber from "../../utils/formatNumber";

const Licensing = () => {
  const [currentPlan, setCurrentPlan] = useState(null);
  const [usageMetrics, setUsageMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [licenseUsage, setLicenseUsage] = useState(null);
  const [timezone, setTimezone] = useState(null);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    current_plan: "",
    expected_plan: "",
    message: "",
  });
  const paginationRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const licenseData = useSelector((state) => state?.license?.data);
  // const usageData = useSelector(
  //   (state) => state?.license?.data?.subscription_usage_details
  // );

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    let tempErrors = {};
    if (!formData.name.trim()) tempErrors.name = "Full Name is required";
    if (!formData.email.trim()) tempErrors.email = "Email is required";
    if (!formData.message.trim()) tempErrors.message = "Message is required";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0; // Returns true if no errors
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault(); // Check if event exists before using it

    if (!validateForm()) return;
    try {
      setLoading(true);
      const response = await contactSales(formData);
      if (response?.status_code === 200) {
        setFormData({});
        showSuccessToast("Request submitted successfully!");
        setOpen(false);
        setLoading(false);
      }
    } catch (error) {
      showErrorToast("Failed to submit request. Please try again.");
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    setLoading(true); // Start loading
    try {
      const currentUser = await fetchUserAttributes();
      setCurrentUser(currentUser);
    } catch (error) {
      console.error("Failed to fetch user:", error);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  useEffect(() => {
    if (open) {
      fetchCurrentUser();
    }
  }, [open]);

  // This effect populates form data after user is fetched
  useEffect(() => {
    if (currentUser) {
      setFormData((prev) => ({
        ...prev,
        name: currentUser.name || "",
        email: currentUser.email || "",
        current_plan: currentPlan?.name || "",
      }));
    }
  }, [currentUser, currentPlan]);

  const getLicenseUsage = async () => {
    try {
      setLoading(true);
      const res = await getLicensingUsageDetails(
        licenseData?.subscription?.subscription_id
      );
      if (res?.status_code === 200) {
        setLicenseUsage(res?.data?.subscription_usage_details);
        setLoading(false);
      } else {
        showErrorToast("Failed to fetch license usage details");
        setLoading(false);
      }
    } catch (err) {
      showErrorToast(
        err.message || "An error occurred while fetching license usage"
      );
      setLoading(false);
    }
  };

  const getWebsiteDetails = async () => {
    setLoading(true);
    try {
      const id = localStorage.getItem("website_id");
      const websiteData = await getWebsiteById(id);
      const timezoneFromApi = websiteData?.data?.time_zone;
      setTimezone(timezoneFromApi);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await getWebsiteDetails();
      getLicenseUsage();
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchLicensingData = async () => {
      try {
        setLoading(true);

        // Ensure licenseData and licenseUsage are available
        if (!licenseData || !licenseUsage) {
          setLoading(false);
          return;
        }

        const currentPlanDetails = {
          id: licenseData?.subscription?.subscription_id,
          name:
            licenseData.plan?.plan_name.charAt(0).toUpperCase() +
            licenseData.plan?.plan_name.slice(1),
          nextBillingDate: licenseData?.subscription?.end_date,
          status: licenseData?.subscription?.status,
        };

        const dataRequestsUsed = Number(licenseUsage?.data_requests_used) || 0;
        const dataRequestsLimit =
          Number(licenseData?.plan?.plan_attributes?.data_requests?.limit) || 0;
        const isSoftLimit =
          licenseData?.plan?.plan_attributes?.data_requests?.is_soft_limit ===
          true;

        const websitesUsed = Number(licenseUsage?.websites_used) || 0;
        const websitesLimit =
          Number(licenseData?.plan?.plan_attributes?.domains?.limit) || 0;
        const isWebsitesSoftLimit =
          licenseData?.plan?.plan_attributes?.domains?.is_soft_limit === true;

        const pageviewUsed = Number(licenseUsage?.page_views_used) || 0;
        const pageViewLimit =
          Number(licenseData?.plan?.plan_attributes?.page_views?.limit) || 0;
        const isPageViewsSoftLimit =
          licenseData?.plan?.plan_attributes?.page_views?.is_soft_limit ===
          true;

        let userData = 0;
        let overageData = 0;
        const limitFun = () => {
          const addedAllUser =
            Number(licenseUsage?.users_used) +
            Number(licenseUsage?.users_overage);

          if (
            Number(licenseData?.plan?.plan_attributes?.users?.limit) >
            addedAllUser
          ) {
            userData = addedAllUser;
            // overageData = Number(licenseData?.plan?.plan_attributes?.users?.limit)
          }
          if (
            Number(licenseData?.plan?.plan_attributes?.users?.limit) <=
            addedAllUser
          ) {
            userData = Number(licenseData?.plan?.plan_attributes?.users?.limit);
            overageData =
              Number(licenseUsage?.users_used) -
              Number(licenseData?.plan?.plan_attributes?.users?.limit) +
              Number(licenseUsage?.users_overage);
          }
        };
        limitFun();

        const licenseUsageMetrics = {
          websites: {
            icon: <Scroll />,
            used: websitesUsed,
            limit: websitesLimit,
            overageAllowed: isWebsitesSoftLimit,
            overageUsed:
              isWebsitesSoftLimit && websitesUsed > websitesLimit
                ? websitesUsed - websitesLimit
                : 0,
            percentage:
              websitesLimit > 0 ? (websitesUsed / websitesLimit) * 100 : 0,
          },
          users: {
            icon: <Profile />,
            // used: usedNum() || 0,
            used: userData || 0,
            limit:
              Number(licenseData?.plan?.plan_attributes?.users?.limit) || 0,
            // overageUsed: overageNum() || 0,
            overageUsed: overageData || 0,
            // overageAllowed: licenseData?.plan?.plan_attributes?.users?.is_soft_limit,
            overageAllowed: overageData > 0,
            // percentage: usageData?.users_used
            //   ? (Number(usageData.users_used) /
            //     Number(licenseData?.plan?.plan_attributes?.users?.limit)) *
            //   100
            //   : 0,
            percentage: userData
              ? (Number(userData) /
                  Number(licenseData?.plan?.plan_attributes?.users?.limit)) *
                100
              : 0,
          },
          integrations: {
            icon: <Hierarchy />,
            used: licenseUsage?.integrations_usage || 0,
            limit: Array.isArray(
              licenseData?.plan?.plan_attributes?.integrations
            )
              ? licenseData?.plan?.plan_attributes?.integrations.length
              : 0,
            overageUsed: Number(licenseUsage?.integrations_overage) || 0, // ← Add this
            overageAllowed: false,
            percentage: licenseUsage?.integrations_usage
              ? (Number(licenseUsage?.integrations_usage) /
                  licenseData?.plan?.plan_attributes?.integrations?.length) *
                100
              : 0,
          },
          dataRequests: {
            icon: <Frame />,
            used: dataRequestsUsed,
            limit: dataRequestsLimit,
            overageUsed:
              isSoftLimit && dataRequestsUsed > dataRequestsLimit
                ? dataRequestsUsed - dataRequestsLimit
                : 0, // ← dynamically calculated overage
            overageAllowed: isSoftLimit, // ← just the soft limit flag
            percentage:
              dataRequestsLimit > 0
                ? (dataRequestsUsed / dataRequestsLimit) * 100
                : 0,
          },
          pageViews: {
            icon: <PageView />,
            used: pageviewUsed,
            limit: pageViewLimit,
            overageUsed:
              isPageViewsSoftLimit && pageviewUsed > pageViewLimit
                ? pageviewUsed - pageViewLimit
                : 0, // ← dynamically calculated overage
            overageAllowed: isPageViewsSoftLimit, // ← just the soft limit flag
            percentage:
              pageViewLimit > 0 ? (pageviewUsed / pageViewLimit) * 100 : 0,
          },
        };

        setCurrentPlan(currentPlanDetails);
        setUsageMetrics(licenseUsageMetrics);
      } catch (err) {
        showErrorToast(
          "Failed to load licensing information. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    // Only run if both licenseData and licenseUsage are available
    if (licenseData && licenseUsage) {
      fetchLicensingData();
    }
    //eslint-disable-next-line
  }, [licenseData, licenseUsage]);

  const formatUsage = (used, limit) => {
    // const formatNumber = (num) => {
    //   if (num >= 1_000_000) {
    //     return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    //   }
    //   if (num >= 1_000) {
    //     return `${(num / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
    //   }
    //   return num;
    // };

    if (limit === -1) {
      return `${formatNumber(used, timezone)} / Custom`;
    }
    return `${formatNumber(used, timezone)} / ${formatNumber(limit, timezone)}`;
  };

  // Create skeleton loading component
  const SkeletonLoader = () => (
    <Box>
      <CommonHeader
        title="Licensing"
        subTitle="Manage your subscription plan and monitor usage"
      />
      
      {/* Plan Header Skeleton */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Skeleton variant="text" width={120} height={28} animation="wave" />
            <Skeleton variant="text" width={80} height={28} animation="wave" sx={{ bgcolor: '#00B89420' }} />
          </Box>
          <Skeleton variant="text" width={200} height={20} animation="wave" />
        </Box>
        <Skeleton variant="rectangular" width={80} height={40} animation="wave" sx={{ borderRadius: '8px' }} />
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Usage Summary Skeleton */}
        <Grid item xs={12} sm={6}>
          <Skeleton variant="text" width={120} height={24} animation="wave" sx={{ mb: 2 }} />
          
          {/* Usage metrics skeleton */}
          {[1, 2, 3, 4, 5].map((item) => (
            <Box key={item} mb={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Skeleton variant="circular" width={24} height={24} animation="wave" />
                  <Skeleton variant="text" width={100} height={20} animation="wave" />
                </Box>
                <Skeleton variant="text" width={80} height={20} animation="wave" />
              </Box>
              <Skeleton 
                variant="rectangular" 
                width="100%" 
                height={12} 
                animation="wave" 
                sx={{ borderRadius: 5, bgcolor: '#00B89420' }} 
              />
            </Box>
          ))}
        </Grid>

        {/* Analytics Panel Skeleton */}
        <Grid item xs={12} sm={6}>
          <Box sx={{ p: 3, borderLeft: "1px solid #000", height: "100%" }}>
            <Skeleton variant="text" width={150} height={24} animation="wave" sx={{ mb: 1 }} />
            <Skeleton variant="text" width={200} height={20} animation="wave" sx={{ mb: 2 }} />
            
            <Box sx={{ height: "280px", overflow: "auto" }}>
              {[1, 2, 3, 4, 5].map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Skeleton variant="text" width={100} height={16} animation="wave" />
                  <Box sx={{ flexGrow: 1, mx: 2 }}>
                    <Skeleton 
                      variant="rectangular" 
                      width="100%" 
                      height={10} 
                      animation="wave"
                      sx={{ borderRadius: 5, bgcolor: '#44bfa320' }}
                    />
                  </Box>
                  <Skeleton variant="text" width={50} height={16} animation="wave" />
                </Box>
              ))}
            </Box>
            
            {/* Pagination skeleton */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
              <Skeleton variant="circular" width={8} height={8} animation="wave" />
              <Skeleton variant="circular" width={8} height={8} animation="wave" />
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Plan Details Skeleton */}
      <Skeleton variant="text" width={160} height={28} animation="wave" sx={{ mb: 2 }} />
      
      <Card sx={{ maxWidth: "100%", mx: "auto", p: 2, borderRadius: 2, mb: 2 }} variant="outlined">
        <CardContent>
          <Box display="flex" gap={4} flexWrap="wrap">
            {/* Inclusion Skeleton */}
            <Box flex={1} minWidth={300}>
              <Skeleton variant="text" width={80} height={24} animation="wave" sx={{ mb: 2 }} />
              {[1, 2, 3, 4].map((item) => (
                <Box key={item} display="flex" alignItems="flex-start" gap={1} mb={1}>
                  <Skeleton variant="circular" width={20} height={20} animation="wave" />
                  <Skeleton variant="text" width="80%" height={16} animation="wave" />
                </Box>
              ))}
            </Box>

            <Divider orientation="vertical" flexItem sx={{ borderColor: "#ccc" }} />

            {/* Exclusion Skeleton */}
            <Box flex={1} minWidth={300}>
              <Skeleton variant="text" width={80} height={24} animation="wave" sx={{ mb: 2 }} />
              {[1, 2, 3].map((item) => (
                <Box key={item} display="flex" alignItems="flex-start" gap={1} mb={1}>
                  <Skeleton variant="circular" width={20} height={20} animation="wave" />
                  <Skeleton variant="text" width="75%" height={16} animation="wave" />
                </Box>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Upgrade Plan Skeleton */}
      <Skeleton variant="text" width={120} height={28} animation="wave" />
      <Paper sx={{ p: 3, mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }} variant="outlined">
        <Skeleton variant="rectangular" width={100} height={80} animation="wave" />
        <Box display="flex" alignItems="center" flexDirection="column" textAlign="center">
          <Skeleton variant="text" width={200} height={32} animation="wave" sx={{ mb: 1 }} />
          <Skeleton variant="text" width={300} height={20} animation="wave" />
        </Box>
        <Skeleton variant="rectangular" width={120} height={50} animation="wave" sx={{ borderRadius: '10px' }} />
      </Paper>
    </Box>
  );

  if (loading) {
    return <SkeletonLoader />;
  }

  if (!licenseData) {
    return (
      <Box p={3}>
        <Typography>No licensing information available.</Typography>
      </Box>
    );
  }

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    backgroundColor: "#F9FAFB",
    borderRadius: "16px",
    boxShadow: 24,
    p: 3,
  };

  const websiteAnalytics = licenseUsage?.website_analytics;

  // const formatNumber = (num) => {
  //   if (num === null || num === undefined) return "0";
  //   const number = Number(num);
  //   if (number >= 1_000_000_000) {
  //     return (number / 1_000_000_000).toFixed(1) + "B";
  //   } else if (number >= 1_000_000) {
  //     return (number / 1_000_000).toFixed(1) + "M";
  //   } else if (number >= 1_000) {
  //     return (number / 1_000).toFixed(1) + "K";
  //   } else {
  //     return number.toLocaleString();
  //   }
  // };

  const keys = Object.keys(licenseUsage?.website_analytics);

  const getMetricValue = (item, type) => {
    return type === 0 ? item.page_visit_counts : item.data_request_counts;
  };

  const ViewType = ["Page Views", "Data Requests"];

  const MAX =
    ViewType[activeStep] === "Page Views"
      ? licenseData?.plan?.plan_attributes?.page_views?.limit || 100
      : licenseData?.plan?.plan_attributes?.data_requests?.limit || 100; // Adjust max value as per your real usage

  // const parseHTMLList = (htmlString) => {
  //   try {
  //     const unescaped = JSON.parse(htmlString); // Unescape the string
  //     const parser = new DOMParser();
  //     const doc = parser.parseFromString(unescaped, "text/html");
  //     const listItems = Array.from(doc.querySelectorAll("li"));
  //     return listItems.map((li) => li.textContent.trim());
  //   } catch (error) {
  //     console.error("Failed to parse HTML:", error);
  //     return [];
  //   }
  // };

  // const inclusions = parseHTMLList(licenseData?.plan?.plan_inclusion);
  // const exclusions = parseHTMLList(licenseData?.plan?.plan_exclusion);

  // const removeEdgeQuotes = (str) => {
  //   return str.replace(/^['"]|['"]$/g, '');
  // };

  const stripHtmlTags = (html) => {
    if (!html) return "";

    // First unescape the string if it's escaped
    let unescaped = html;
    try {
      unescaped = JSON.parse(html);
    } catch (e) {
      unescaped = html;
    }

    // Replace <br> tags with newlines and remove other HTML tags
    const text = unescaped
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/?p>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/\n+/g, "\n") // Remove multiple newlines
      .trim();

    return text;
  };

  // Remove all HTML tags and replace <br> with newlines

  const inclusions = stripHtmlTags(licenseData?.plan?.plan_inclusion);
  const exclusions = stripHtmlTags(licenseData?.plan?.plan_exclusion);
  // console.log('inclusions', inclusions, licenseData?.plan?.plan_inclusion);

  // Remove all HTML tags and replace <br> with newlines

  return (
    <Box>
      <CommonHeader
        title="Licensing"
        subTitle="Manage your subscription plan and monitor usage"
      />
      {/* Plan Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ mb: 0 }}>
              Current Plan:
            </Typography>
            <Typography color="#00B894" variant="h6" fontWeight={600}>
              {currentPlan?.name}
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary">
            Next billing date: {currentPlan?.nextBillingDate}
          </Typography>
        </Box>
        <Chip
          label={currentPlan?.status === "active" ? "Active" : "Inactive"}
          color={currentPlan?.status === "active" ? "primary" : "default"}
          variant="outlined"
          sx={{
            fontWeight: "bold",
            borderRadius: "8px",
            fontSize: "15px",
            height: "40px",
          }}
          icon={
            <Circle
              sx={{
                fontSize: 12,
                color:
                  currentPlan?.status === "active"
                    ? "primary.main"
                    : "grey.500",
                // marginLeft: '4px'
              }}
            />
          }
        />
      </Box>

      <Grid
        container
        spacing={2}
        sx={{
          display: "flex",
          alignItems: "stretch",
          height: "100%",
          mb: 2,
        }}
      >
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            height: "100%",
          }}
        >
          {/* Usage Summary */}
          <Typography variant="subtitle1" fontWeight="medium">
            Usage Summary
          </Typography>

          {Object.entries(usageMetrics).map(([key, metric]) => {
            const formattedKey = key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase());

            return (
              <Box key={key} mb={1}>
                {/* Top section: icon + label + usage */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  {/* Icon + Text */}
                  <Box display="flex" alignItems="center" gap={1}>
                    {metric.icon}
                    <Typography
                      variant="body2"
                      color="#525455"
                      fontWeight="bold"
                    >
                      {formattedKey}
                    </Typography>
                    {metric.overageAllowed && metric.overageUsed > 0 && (
                      <Chip
                        label={`+${metric.overageUsed} Over`}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          backgroundColor: "#FF7878",
                          color: "white",
                        }}
                      />
                    )}
                  </Box>

                  {/* Usage */}
                  <Typography
                    variant="body1"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {formatUsage(metric.used, metric.limit, timezone)}
                  </Typography>
                </Box>

                {/* Progress bar container with overage chip on top-right */}
                <Box>
                  {/* Progress bar */}
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(metric.percentage, 100)}
                    sx={{
                      height: 12,
                      borderRadius: 5,
                      backgroundColor: "#D9D9D9",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor:
                          metric.percentage > 90
                            ? "#d32f2f"
                            : metric.percentage > 70
                            ? "#ed6c02"
                            : (theme) => theme.palette.primary.main,
                      },
                      marginTop: "10px",
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Grid>

        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              p: 3,
              borderLeft: "1px solid #000",
              height: "100%",
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Total {ViewType[activeStep]}
            </Typography>
            <Typography variant="h6" color="text.secondary" mb={2}>
              {ViewType[activeStep] === "Page Views"
                ? `${formatNumber(
                    licenseUsage?.page_views_used,
                    timezone
                  )}/ ${formatNumber(
                    licenseData?.plan?.plan_attributes?.page_views?.limit,
                    timezone
                  )}`
                : `${formatNumber(
                    licenseUsage?.data_requests_used,
                    timezone
                  )}/ ${formatNumber(
                    licenseData?.plan?.plan_attributes?.data_requests?.limit,
                    timezone
                  )}`}
            </Typography>

            {/* Scrollable swiper content */}
            <Box sx={{ height: "280px", overflow: "auto" }}>
              <Swiper
                modules={[Navigation, Pagination]}
                pagination={{
                  clickable: true,
                  el: ".custom-swiper-pagination",
                }}
                onSlideChange={(swiper) => setActiveStep(swiper.activeIndex)}
                initialSlide={activeStep}
                style={{ width: "100%" }}
                onSwiper={(swiper) => {
                  if (paginationRef.current) {
                    swiper.params.pagination.el = paginationRef.current;
                    swiper.pagination.init();
                    swiper.pagination.render();
                    swiper.pagination.update();
                  }
                }}
              >
                {ViewType.map((type, index) => (
                  <SwiperSlide key={index}>
                    <Stack spacing={2}>
                      {keys.map((key, idx) => {
                        const value = getMetricValue(
                          websiteAnalytics[key],
                          index
                        );

                        return (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Tooltip title={new URL(key).hostname} arrow>
                              <Typography
                                sx={{ width: 100 }}
                                variant="body2"
                                noWrap
                              >
                                {new URL(key).hostname}
                              </Typography>
                            </Tooltip>
                            <Box sx={{ flexGrow: 1, mx: 2 }}>
                              <LinearProgress
                                variant="determinate"
                                value={(value / MAX) * 100}
                                sx={{
                                  height: 10,
                                  borderRadius: 5,
                                  backgroundColor: "#e0e0e0",
                                  "& .MuiLinearProgress-bar": {
                                    backgroundColor: "#44bfa3",
                                  },
                                }}
                              />
                            </Box>
                            <Typography
                              sx={{ textAlign: "right" }}
                              variant="body2"
                            >
                              {formatNumber(value, timezone)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>

            {/* Pagination bullets (outside scroll area) */}
            <Box
              className="custom-swiper-pagination"
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "center",
                gap: 1,
                ".swiper-pagination-bullet": {
                  backgroundColor: "#ccc",
                  opacity: 1,
                },
                ".swiper-pagination-bullet-active": {
                  backgroundColor: "#44bfa3",
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <Typography variant="h6" mb={2} color="#525455" fontWeight={700}>
        Your Current Plan
      </Typography>

      <Card
        sx={{ maxWidth: "100%", mx: "auto", p: 2, borderRadius: 2, mb: 2 }}
        variant="outlined"
      >
        <CardContent>
          <Box display="flex" gap={4} flexWrap="wrap">
            {/* Inclusion */}
            <Box flex={1} minWidth={300}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                color="#525455"
              >
                Inclusion
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {inclusions
                  .split("\n")
                  .filter((line) => line.trim())
                  .map((line, index) => (
                    <Box
                      key={`inclusion-${index}`}
                      display="flex"
                      alignItems="flex-start"
                      gap={1}
                    >
                      <CheckCircleOutline
                        fontSize="small"
                        sx={{ color: "primary.main", mt: "2px" }}
                      />
                      <Typography variant="body2">{line.trim()}</Typography>
                    </Box>
                  ))}
              </Box>
            </Box>

            {/* Divider */}
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: "#ccc" }}
            />

            {/* Exclusion */}
            <Box flex={1} minWidth={300}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                gutterBottom
                color="#525455"
              >
                Exclusion
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {exclusions
                  .split("\n")
                  .filter((line) => line.trim())
                  .map((line, index) => (
                    <Box
                      key={`exclusion-${index}`}
                      display="flex"
                      alignItems="flex-start"
                      gap={1}
                    >
                      <HighlightOff
                        fontSize="small"
                        sx={{ color: "error.main", mt: "2px" }}
                      />
                      <Typography variant="body2">{line.trim()}</Typography>
                    </Box>
                  ))}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Typography variant="h6" color="#525455" fontWeight="bold">
        Upgrade Plan
      </Typography>
      <Paper
        sx={{
          p: 3,
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
        variant="outlined"
      >
        <Upgrade height="100%" width="20%" />
        <Box
          display="flex"
          alignItems="center"
          flexDirection="column"
          textAlign="center"
        >
          <Typography variant="h5" fontWeight="bold" color="#525455" mb={2}>
            Want more Features?
          </Typography>
          <Typography variant="body" color="#989898">
            Please upgrade to a higher plan to get more features.
          </Typography>
        </Box>
        <Button
          variant="contained"
          sx={{
            borderRadius: "10px",
            height: {
              xs: "40px",
              sm: "45px",
              md: "50px",
            },
            color: "white",
            textTransform: "capitalize",
            fontWeight: 700,
            fontSize: {
              xs: "14px",
              sm: "15px",
              md: "17px",
            },
            paddingLeft: {
              xs: "10px",
              sm: "15px",
              md: "20px",
            },
            paddingRight: {
              xs: "10px",
              sm: "15px",
              md: "20px",
            },
            whiteSpace: "nowrap", // Prevent text wrapping
            minWidth: {
              xs: "auto",
              sm: "120px",
              md: "150px",
            }, // Optional: give enough room to avoid wrapping
          }}
          onClick={() => setOpen(true)}
        >
          Contact Sales
        </Button>
      </Paper>

      <Modal open={open}>
        <Box sx={style}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" color="primary" fontWeight="bold">
              Contact Sales
            </Typography>
            <IconButton
              onClick={() => {
                setOpen(false);
                setErrors({});
              }}
            >
              <CloseOutlined />
            </IconButton>
          </Box>

          <Box sx={{ borderRadius: "8px", mt: 2 }}>
            <InputLabel sx={{ display: "block", marginTop: 1 }} required>
              Full Name
            </InputLabel>
            <TextField
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              sx={{ marginTop: 1, mb: 1 }}
              size="small"
              disabled
            />

            <InputLabel sx={{ display: "block", marginTop: 2 }} required>
              Email
            </InputLabel>
            <TextField
              name="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              sx={{ marginTop: 1, mb: 1 }}
              size="small"
              disabled
            />

            <InputLabel sx={{ display: "block", marginTop: 2 }} required>
              Current Plan
            </InputLabel>
            <TextField
              name="current_plan"
              disabled
              value={currentPlan?.name}
              onChange={handleChange}
              fullWidth
              sx={{ marginTop: 1, mb: 1 }}
              size="small"
            />

            <InputLabel sx={{ display: "block", marginTop: 2 }} required>
              Message
            </InputLabel>
            <TextField
              name="message"
              value={formData.message}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              sx={{ marginTop: 1, mb: 1 }}
              size="small"
              error={!!errors.message}
              helperText={errors.message}
            />

            <Box sx={{ display: "flex", gap: 2, marginTop: 2 }}>
              <ButtonCommon onClick={handleSubmit}>
                {loading ? <CircularProgress size={20} /> : "Submit"}
              </ButtonCommon>
              <ButtonCommon onClick={() => setOpen(false)}>Cancel</ButtonCommon>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Licensing;
